import { useEffect, useRef } from 'react';
import { analyticsApiRef, useApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useLocation } from 'react-router-dom';
import { classifyUsageRoute, type UsageRouteEvent } from './usageRoutes';

const stringDimension = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const externalLinkDimensions = (href: string) => {
  try {
    const url = new URL(href, window.location.href);
    if (!['http:', 'https:'].includes(url.protocol)) {
      return undefined;
    }
    if (url.origin === window.location.origin) {
      return undefined;
    }

    return {
      link_host: url.hostname,
      link_url: `${url.origin}${url.pathname}`,
    };
  } catch {
    return undefined;
  }
};

export const UsageAnalyticsRouteTracker = () => {
  const analyticsApi = useApi(analyticsApiRef);
  const catalogApi = useApi(catalogApiRef);
  const location = useLocation();
  const previousRoute = useRef<UsageRouteEvent>();
  const currentRoute = useRef<UsageRouteEvent>();
  const trackedEntityMetadata = useRef(new Set<string>());

  useEffect(() => {
    const route = classifyUsageRoute(location.pathname);
    const previous = previousRoute.current;

    analyticsApi.captureEvent({
      action: route.event_type,
      attributes: {
        ...route,
        ...(previous
          ? {
              previous_route_group: previous.route_group,
              previous_route_pattern: previous.route_pattern,
            }
          : {}),
      },
      subject: route.route_pattern,
    } as any);

    previousRoute.current = route;
    currentRoute.current = route;
  }, [analyticsApi, location.pathname]);

  useEffect(() => {
    const route = classifyUsageRoute(location.pathname);
    if (!route.entity_ref || !route.route_group.startsWith('catalog_entity')) {
      return undefined;
    }
    if (trackedEntityMetadata.current.has(route.entity_ref)) {
      return undefined;
    }

    let cancelled = false;

    catalogApi
      .getEntityByRef(route.entity_ref)
      .then(entity => {
        if (cancelled || !entity) {
          return;
        }

        trackedEntityMetadata.current.add(route.entity_ref!);
        const spec = (entity.spec ?? {}) as Record<string, unknown>;

        analyticsApi.captureEvent({
          action: 'catalog_entity_metadata',
          attributes: {
            entity_domain: stringDimension(spec.domain),
            entity_kind: route.entity_kind,
            entity_lifecycle: stringDimension(spec.lifecycle),
            entity_owner: stringDimension(spec.owner),
            entity_ref: route.entity_ref,
            entity_system: stringDimension(spec.system),
            event_type: 'catalog_entity_metadata',
            route_group: route.route_group,
            route_pattern: route.route_pattern,
          },
          subject: route.entity_ref,
        } as any);
      })
      .catch(() => {
        // Ignore enrichment failures. Analytics must not affect navigation.
      });

    return () => {
      cancelled = true;
    };
  }, [analyticsApi, catalogApi, location.pathname]);

  useEffect(() => {
    const trackExternalLinkClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const anchor = event.target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) {
        return;
      }

      const link = externalLinkDimensions(anchor.href);
      if (!link) {
        return;
      }

      const route =
        currentRoute.current ?? classifyUsageRoute(window.location.pathname);
      analyticsApi.captureEvent({
        action: 'external_link_click',
        attributes: {
          event_type: 'external_link_click',
          link_group: route.route_group,
          ...link,
          route_group: route.route_group,
          route_pattern: route.route_pattern,
        },
        subject: link.link_host,
      } as any);
    };

    document.addEventListener('click', trackExternalLinkClick, {
      capture: true,
    });

    return () => {
      document.removeEventListener('click', trackExternalLinkClick, {
        capture: true,
      });
    };
  }, [analyticsApi]);

  return null;
};
